import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { useForm } from "react-hook-form";
import * as z from "zod";
import Input from "../Form/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import {
  batteryToFormState,
  emptyBatteryFormState,
  formatDensity
} from "../../utils/batteryUtils";

const schema = z.object({
  comments: z.string(),
  hour_meter: z.any().optional(),
  internal_notes: z.string().max(10000, { message: "Máximo de 10000 caracteres." }).optional()
});

const ChangeObsPreventive = ({
  currentPreventive,
  onHide = () => {},
  visible = true,
  setLoading,
  getPreventivas
}) => {
  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    watch
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {},
    shouldFocusError: false
  });

  const [toastOptions, setToastOptions] = useState({});
  const toast = useRef(null);
  const { access_type } = useAuth();

  const [batteries, setBatteries] = useState([emptyBatteryFormState()]);

  const isGLP =
    currentPreventive?.preventive_type === "EmpilhadeiraCombustao" ||
    currentPreventive?.preventive_type === "EmpilhadeiraEletricaDeContrapeso";

  useEffect(() => {
    if (currentPreventive) {
      setValue("comments", currentPreventive?.comments);
      setValue("hour_meter", currentPreventive?.hour_meter || "");
      setValue("internal_notes", currentPreventive?.internal_notes || "");
      if (!isGLP) {
        const batteriesFromApi = currentPreventive?.batteries;
        if (batteriesFromApi?.length > 0) {
          setBatteries(batteriesFromApi.map((b) => batteryToFormState(b)));
        } else if (
          currentPreventive?.brand ||
          currentPreventive?.pat ||
          currentPreventive?.manufacture_date
        ) {
          setBatteries([batteryToFormState(currentPreventive)]);
        } else {
          setBatteries([emptyBatteryFormState()]);
        }
      } else {
        setBatteries([emptyBatteryFormState()]);
      }
    }
  }, [currentPreventive, isGLP]);

  const hideForm = () => {
    onHide();
  };
  const submitForm = async (data) => {
    if (!currentPreventive) {
      return;
    }
    setLoading(true);
    const payload = {
      report_type: isGLP ? "preventive_combustion" : "preventive",
      comments: data.comments
    };
    if (access_type !== "Costumer" && data.internal_notes !== undefined) {
      payload.internal_notes = data.internal_notes;
    }
    if (access_type === "Admin" && data.hour_meter !== undefined) {
      payload.hour_meter = data.hour_meter;
    }
    if (!isGLP && currentPreventive.equipment_id) {
      payload.equipment_id = currentPreventive.equipment_id;
      payload.batteries = batteries.map((bat) => {
        const item = {
          brand: bat.brand,
          pat: bat.brand,
          manufacture_date: bat.manufactureDate,
          reading_date: bat.readingDate,
          total_voltage: bat.totalVoltage,
          voltagem_nominal: bat.elementCount === 12 ? 24 : 48
        };
        bat.elements.slice(0, bat.elementCount).forEach((el, idx) => {
          item[`element_${idx + 1}_voltage`] = el.voltage;
          item[`element_${idx + 1}_density`] = el.density;
        });
        return item;
      });
    }
    await api.put(
      `/preventive/update-observation/${currentPreventive.id}`,
      payload
    );
    setLoading(false);
    toast.current.show({
      severity: "success",
      summary: "",
      detail: "Dados alterados com sucesso.",
      life: 5000
    });
    onHide();
    getPreventivas();
  };

  const footer = (
    <React.Fragment>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => {
          onHide();
        }}
      />
      <Button
        label={"Salvar"}
        icon="pi pi-check"
        className="p-button-text"
        onClick={handleSubmit((data) => submitForm(data))}
      />
    </React.Fragment>
  );

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        style={{ marginTop: 60, width: "80rem" }}
        className="p-fluid"
        modal
        onHide={hideForm}
        header={"Alterar observação da preventiva"}
        footer={footer}
      >
        <form action="">
          <Input
            error={errors.comments?.message}
            name={"comments"}
            label={"Observações"}
            register={register}
          />
          {access_type !== "Costumer" && (
            <>
              <Input
                error={errors.internal_notes?.message}
                name={"internal_notes"}
                label={"Informações de uso interno"}
                register={register}
                asTextArea
                rows={4}
                maxLength={10000}
              />
              <div className="d-flex justify-content-end small text-muted">
                {(watch("internal_notes")?.length || 0)}/10000
              </div>
            </>
          )}
          {access_type === "Admin" && (
            <Input
              error={errors.hour_meter?.message}
              name={"hour_meter"}
              label={"Horímetro"}
              register={register}
              type="number"
            />
          )}
          {!isGLP && (
            <div className="row mt-4">
              <div className="col-12">
                <h5>
                  Medição de Bateria
                  {batteries.length > 1 && ` (${batteries.length} baterias)`}
                </h5>
              </div>
              {batteries.map((bat, batIdx) => (
                <div key={batIdx} className="col-12 border rounded p-3 mb-3">
                  <h6 className="mb-3">
                    {batteries.length > 1
                      ? `Bateria ${batIdx + 1}`
                      : "Dados da bateria"}
                  </h6>
                  <div className="row">
                    <div className="col-md-2 mb-2">
                      <label className="form-label">PAT</label>
                      <input
                        type="text"
                        className="form-control"
                        value={bat.brand}
                        onChange={(e) =>
                          setBatteries((prev) => {
                            const u = [...prev];
                            u[batIdx] = { ...u[batIdx], brand: e.target.value };
                            return u;
                          })
                        }
                      />
                    </div>
                    <div className="col-md-2 mb-2">
                      <label className="form-label">Tensão total (V)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={bat.totalVoltage}
                        onChange={(e) =>
                          setBatteries((prev) => {
                            const u = [...prev];
                            u[batIdx] = {
                              ...u[batIdx],
                              totalVoltage: e.target.value
                            };
                            return u;
                          })
                        }
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="row mt-2">
                    {bat.elements.slice(0, bat.elementCount).map((el, idx) => (
                      <div key={idx} className="col-md-2 mb-2">
                        <div className="border rounded p-2">
                          <div className="fw-bold small mb-1">Elem. {idx + 1}</div>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control mb-1 form-control-sm"
                            placeholder="V"
                            value={el.voltage}
                            onChange={(e) =>
                              setBatteries((prev) => {
                                const u = [...prev];
                                u[batIdx].elements = [...u[batIdx].elements];
                                u[batIdx].elements[idx] = {
                                  ...u[batIdx].elements[idx],
                                  voltage: e.target.value
                                };
                                return u;
                              })
                            }
                          />
                          <input
                            type="number"
                            step="0.001"
                            className="form-control form-control-sm"
                            placeholder="0.000"
                            value={el.density}
                            onChange={(e) =>
                              setBatteries((prev) => {
                                const u = [...prev];
                                u[batIdx].elements = [...u[batIdx].elements];
                                u[batIdx].elements[idx] = {
                                  ...u[batIdx].elements[idx],
                                  density: e.target.value
                                };
                                return u;
                              })
                            }
                            onBlur={(e) => {
                              const formatted = formatDensity(e.target.value);
                              if (formatted !== "" && formatted !== e.target.value) {
                                setBatteries((prev) => {
                                  const u = [...prev];
                                  u[batIdx].elements = [...u[batIdx].elements];
                                  u[batIdx].elements[idx] = {
                                    ...u[batIdx].elements[idx],
                                    density: formatted
                                  };
                                  return u;
                                });
                              }
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </form>
      </Dialog>
    </>
  );
};

export default ChangeObsPreventive;
